import { FC, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from 'xlsx';
import Flatpickr from "react-flatpickr";
import { Vietnamese } from "flatpickr/dist/l10n/vn.js"
import { PaginateModel } from "../../../models/PaginateModel";
import { SaleRowCompareModel, SaleRowModel, SaleRowSearchModel } from "../../../models/account-books/SalesAccount";
import SalesAccountTable from "../components/SalesAccountTable";
import { PaginationLength } from "../../../../_metronic/partials/pagination/PaginationLength";
import { Paginate } from "../../../../_metronic/partials/pagination/Paginate";
import { DEFAULT_PER_PAGE } from "../../../constants/common";
import ConfigModal from "../../../components/Modal/ConfigModal";
import { HeaderModel, ModalModel } from "../../../models/CommonModel";
import { exportSalesBooks, getMisingSalesBooks, getSalesBooks } from "../AccountBooksCRUD";
import { download, Toast } from "../../../modules/common/helper";
import moment from "moment";
import { useIntl } from "react-intl";
import useMasterData from "../../../hooks/useMaster";
import { MstTaxGroupModel } from "../../../models/MstTaxGroupModel";
import { formatPrice } from "../../../selectors";
import CompareModal from "../components/CompareModal";

interface ExcelRow {
  [key: string]: any;
}

interface Props {
  opening?: string
  closing?: string
  changeTab: (tab: string) => void
  isFullYear: boolean
}

const SalesAccount: FC<Props> = ({ opening, closing, changeTab, isFullYear }) => {
  const { master } = useMasterData();
  const initSearch = useMemo(() => ({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    start_date: opening,
    end_date: closing,
    tax_group_id: ''
  }), [opening, closing])
  const fp = useRef<any>(null);
  const intl = useIntl();
  const [search, setSearch] = useState<SaleRowSearchModel>(initSearch)
  const [data, setData] = useState<PaginateModel<SaleRowModel>>()
  const [subTotal, setSubTotal] = useState<number>(0)
  const [configModal, setConfigModal] = useState<ModalModel>({
    show: false,
    mode: 'add',
  });
  const [compareModal, setCompareModal] = useState<ModalModel>({
    show: false,
    mode: 'add',
  });
  const [hideCols, setHideCols] = useState<string[]>([
    'status'
  ]);
  const header: HeaderModel[] = [
    {
      label: 'Ngày ghi sổ',
      field: 'created_at',
      style: { minWidth: "90px", },
      sortable: true
    },
    {
      label: 'Số hóa đơn',
      field: 'invoice_no',
      style: { minWidth: "90px", },
      sortable: true
    },
    {
      label: 'Ngày xuất HĐ',
      field: 'invoice_date',
      style: { minWidth: "90px", },
      sortable: true
    },
    {
      label: 'Tên sản phẩm',
      field: 'product_name',
      style: { minWidth: "120px", },
      sortable: true
    },
    {
      label: 'Số lượng',
      field: 'quantity',
      style: { minWidth: "90px", textAlign: "center" },
      sortable: true
    },
    {
      label: 'Số tiền',
      field: 'line_amount',
      style: { minWidth: "120px", textAlign: "center" },
      sortable: true
    },
    {
      label: 'Nhóm ngành',
      field: 'tax_group',
      style: { minWidth: "120px", },
      sortable: true
    },
    {
        label: 'Trạng thái',
        field: 'status',
        style: { minWidth: "100px", },
        sortable: false
    }
  ]

  const handleChangeVisCols = (e: any) => {
    let hiddenCols = [...hideCols];
    if (!e.target?.checked) {
      setHideCols([...hiddenCols, e.target.name])
    } else {
      setHideCols(hiddenCols.filter((item) => item !== e.target.name))
    }
  }

  const [compareRows, setCompareRows] = useState<SaleRowCompareModel[]>([]);
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const openInputFile = () => {
    fileInputRef.current?.click();
  };
  const excelDateToJSDate = (serial: number) => {
    const excelEpoch = new Date(1899, 11, 30);
    let date =  new Date(excelEpoch.getTime() + serial * 86400000);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;

  };

const parseExcelDate = (value: number) => {
  const parsed = XLSX.SSF.parse_date_code(value);

  if (!parsed) return '';

  const { y, m, d } = parsed;

  return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
};

const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const files = e.target.files;

  if (!files?.[0]) return;

  const file = files[0];
  const reader = new FileReader();

  reader.onload = (evt) => {
    const bstr = evt.target?.result;

    const wb = XLSX.read(bstr, { type: 'binary' });
    const ws = wb.Sheets[wb.SheetNames[0]];

    const rawData: any[] = XLSX.utils.sheet_to_json(ws);

    const data: SaleRowCompareModel[] = rawData.map((row) => ({
        invoice_code: row['Mã HĐ'],
        invoice_no: row['Số HĐ'],
        invoice_date: parseExcelDate(row['Ngày HĐ']),
        product_name: row['Tên hàng hóa dịch vụ'],
        quantity: Number(row['Số lượng'] || 0),
        unit_price: Number(row['Đơn giá'] || 0),
        vat: Number(row['VAT'] || 0),
        line_amount: Number(row['Thành tiền'] || 0),
      }));
    setCompareRows(data);
    getMisingSalesBooksData(data)
  };

  reader.readAsBinaryString(file);
  setFile(file);
};


  useEffect(() => {
    if (opening && closing) {
      getData({ ...initSearch, start_date: opening, end_date: closing });
    }
  }, [opening, closing, initSearch])

  // useEffect(() => {
  //     if (search?.start_date && search?.end_date) {
  //         getData({...search});
  //     }
  // }, [search])

  const mapMissingToSaleRow = (row: SaleRowCompareModel): SaleRowModel => {
    return {
      id: 0, // fake
      store_id: 0,
      order_id: 0,
      detail_id: 0,
      created_at: '',
      invoice_no: row.invoice_code + row.invoice_no,
      invoice_date: row.invoice_date,
      product_id: 0,
      product_code: '',
      product_name: row.product_name,
      quantity: row.quantity,
      unit_price: row.unit_price,
      line_amount: row.line_amount,
      is_return: 0,
      return_qty: 0,
      tax_group: '',
      mst_tax_group: {} as any,
      status: 'not_yet'
    };
  };
  const [missingRows, setMissingRows] = useState<SaleRowCompareModel[]>();
  const getMisingSalesBooksData = async (compareRows: SaleRowCompareModel[]) => {
    if (!data) {
      return;
    }
    try {
      const { data: resData } = await getMisingSalesBooks(search, compareRows);
      const missingLines: SaleRowCompareModel[] = resData?.data ?? [];
      const currentLines: SaleRowModel[] = [...(data?.data ?? [])];

      const mappedMissing = missingLines.map(mapMissingToSaleRow);

      const merged = [...currentLines, ...mappedMissing];

      merged.sort((a, b) => {
        const aNo = a.invoice_no?.trim();
        const bNo = b.invoice_no?.trim();

        if (!aNo && !bNo) return 0;

        if (!aNo) return 1;

        if (!bNo) return -1;

        return aNo.localeCompare(bNo, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
      
      let cloneData = {
        ...data,
        data: merged
      }
      setData(cloneData)
    } catch (e: any) {
      console.log(e)
      Toast.fire({
        icon: 'error',
        title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
      })
    }
  }

  const getData = async (searchParams: SaleRowSearchModel) => {
    try {
      const { data: resData } = await getSalesBooks(searchParams, compareRows);
      setData(resData.paginate_data);
      setSubTotal(resData.sub_total);
      setSearch(searchParams)
    } catch (e: any) {
      Toast.fire({
        icon: 'error',
        title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
      })
    }
  }

  const handleSearch = (e: any) => {
    e.preventDefault();
    getData({ ...search, page: 1 })
  }

  const handleClearSearch = (e: any) => {
    if (fp.current?.flatpickr) {
      fp.current.flatpickr.setDate([
        moment(opening).format('DD-MM-YYYY'),
        moment(closing).format('DD-MM-YYYY')
      ]);
    }
    getData({ ...initSearch })
  }
  const handlePageLengthChange = (e: any) => {
    getData({ ...search, page: 1, per_page: +e.target.value })
  }

  const handlePageChange = (data: any) => {
    getData({ ...search, page: data.selected + 1 })
  }

  const handleSort = (sortBy: string, direction: 'asc' | 'desc' | 'none') => {
    const currentSearch = { ...search };
    const sortParams: SaleRowSearchModel = { ...currentSearch, sort_by: sortBy, sort_direction: direction };
    if (direction === 'none') {
      delete sortParams.sort_by;
      delete sortParams.sort_direction;
    }
    getData({ ...sortParams });
  }

  const handleExport = async () => {
    const { data } = await exportSalesBooks({ ...search });
    download(data, '4-So Doanh thu');
  }

  const showCompareModal = (originData: SaleRowModel, compareData: SaleRowModel) => {
    setCompareModal({
      ...compareModal,
      show: true,
      data: {
        origin_data: originData,
        compare_data: compareData
      }
    })
  }

  const handleUpdateData = (newData: SaleRowModel) => {
    setCompareModal(prev => ({
      ...prev,
      data: {
        ...prev.data,
        origin_data: newData,
      },
    }));
  };

  const handleVerified = () => {

  }

  return <>
    <div className='card mt-3'>
      <div className="card-header card-header-stretch p-3">
        <h3 className="card-title">
          <button type="button" className="btn btn-primary btn-sm mx-1 mb-1 me-2" onClick={() => changeTab('index')}>
            <i className="fas fa-angle-left" />
          </button>
          Sổ doanh thu
        </h3>
        <div className="card-toolbar align-items-center">
          <div className="btn-groups">
            {!isFullYear && (
              <button className="btn ms-auto btn-sm btn-primary text-hover-white" type="button"
                onClick={handleExport}>
                Tải xuống
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="card-header p-3">
        <div className="col-md-12">
          <form onSubmit={handleSearch}>
            <div className="row">
              <div className='col-md-3 mb-2'>
                <label className="form-label">Tên sản phẩm</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder=""
                  name="product_name"
                  autoComplete='nope'
                  value={search?.product_name ?? ''}
                  onChange={(e) => setSearch({ ...search, product_name: e.target.value })}
                />
              </div>
              <div className='col-md-3 mb-2'>
                <label className="form-label">Khoảng thời gian</label>
                <Flatpickr
                  ref={fp}
                  onChange={(date) => {
                    let startDate = moment(date[0]).format('YYYY-MM-DD');
                    let endDate = moment(date[1]).format('YYYY-MM-DD');
                    setSearch({ ...search, start_date: startDate, end_date: endDate });
                  }}
                  options={{
                    mode: "range",
                    dateFormat: "d-m-Y",
                    locale: Vietnamese,
                    shorthandCurrentMonth: true,
                    minDate: moment(opening).format('DD-MM-YYYY'),
                    maxDate: moment(closing).format('DD-MM-YYYY'),
                    defaultDate: [moment(opening).format('DD-MM-YYYY'), moment(closing).format('DD-MM-YYYY')],
                  }}
                  className='form-control'
                  placeholder=''
                />
              </div>
              <div className='col-md-3 mb-2'>
                <label className="form-label">Số hóa đơn</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder=""
                  name="invoice_no"
                  autoComplete='nope'
                  value={search?.invoice_no ?? ''}
                  onChange={(e) => setSearch({ ...search, invoice_no: e.target.value })}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label className="form-label">Nhóm ngành</label>
                <select
                  className="form-select"
                  value={search.tax_group_id}
                  onChange={(e) => setSearch({ ...search, tax_group_id: parseInt(e.target.value) })}
                  name="status"
                >
                  <option value="">Tất cả</option>
                  {master?.mst_tax_groups?.map((el: MstTaxGroupModel) => {
                    return <option value={el.id}>{el.name}</option>
                  })}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="btns-group">
                <button
                  className="col-auto btn btn-sm btn-primary mt-2 me-2"
                  onClick={handleSearch}
                >
                  <i className="bi bi bi-search fs-4"></i> {intl.formatMessage({ id: 'COMMON.SEARCH' })}
                </button>
                <button
                  className="col-auto btn btn-sm btn-danger mt-2 me-2"
                  type="button"
                  onClick={handleClearSearch}
                >
                  <i className="bi bi-eraser-fill fs-1"></i> Re-search
                </button>
                <button
                  className="d-none col-auto btn btn-sm btn-outline border-primary text-primary mt-2 me-2"
                  type="button"
                  onClick={openInputFile}
                >
                  <i className="bi bi bi-cloud-arrow-up-fill fs-1 text-primary"></i> Nhập đầu ra
                </button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className='card-body p-3'>
        <div className="justify-content-between align-items-center my-3 row">
          <div className='col-12 '>
            <h3>Tổng giá trị: <span className="text-bold">{formatPrice(subTotal)}</span></h3>
          </div>
        </div>
        <SalesAccountTable
          data={data?.data || []}
          compareRows={compareRows}
          openColConfig={() => setConfigModal({ ...configModal, show: true })}
          header={header}
          hideCols={hideCols}
          handleSort={handleSort}
          showCompareModal={showCompareModal}
        />
      </div>
      <div className='card-footer d-flex justify-content-between paginate-wrapper'>
        <PaginationLength selected={search.per_page || DEFAULT_PER_PAGE} onChange={handlePageLengthChange} />
        <Paginate
          pageCount={data?.meta?.last_page || 0}
          onPageChange={handlePageChange}
          forcePage={(search.page || 1) - 1}
        />
      </div>
      <ConfigModal
        {...configModal}
        header={header}
        hideCols={hideCols}
        handleChangeVisCols={handleChangeVisCols}
        onClose={() => { setConfigModal({ ...configModal, show: false }) }}
      />
      <CompareModal
        {...compareModal}
        handleVerified={handleVerified}
        onClose={() => setCompareModal({...compareModal, show: false, data: null})}
        handleUpdateData={handleUpdateData}
      />
    </div>
  </>
}

export default SalesAccount;